import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://api.utiliti.ng";

// Create axios instance with authentication
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for authentication
axiosInstance.interceptors.request.use(
  (config) => {
    const user = localStorage.getItem('user');
    let token = '';
    if (user) {
      try {
        token = JSON.parse(user).token;
      } catch (e) { }
    }
    if (token) {
      config.headers['x-auth-token'] = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

const savingsService = {
  // Get all savings with pagination and filtering
  getAllSavings: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/api/savings/admin/all', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get savings statistics
  getSavingsStats: async (period = 'month') => {
    try {
      const response = await axiosInstance.get('/api/savings/admin/stats', {
        params: { period }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get user savings
  getUserSavings: async (userId) => {
    try {
      const response = await axiosInstance.get(`/api/savings/admin/user/${userId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get savings by ID
  getSavingsById: async (savingsId) => {
    try {
      const response = await axiosInstance.get(`/api/savings/admin/${savingsId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get all savings transactions
  getAllSavingsTransactions: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/api/savings/admin/transactions/all', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update savings balance
  updateSavingsBalance: async (savingsId, data) => {
    try {
      const response = await axiosInstance.put(`/api/savings/admin/${savingsId}/balance`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update savings status
  updateSavingsStatus: async (savingsId, status) => {
    try {
      const response = await axiosInstance.put(`/api/savings/admin/${savingsId}/status`, { status });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Delete savings
  deleteSavings: async (savingsId) => {
    try {
      const response = await axiosInstance.delete(`/api/savings/admin/${savingsId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update fixed savings maturity
  updateFixedSavingsMaturity: async (savingsId, data) => {
    try {
      const response = await axiosInstance.put(`/api/savings/admin/fixed/${savingsId}/maturity`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get pending withdrawals
  getPendingWithdrawals: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/api/savings/admin/withdrawals/pending', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Approve withdrawal
  approveWithdrawal: async (transactionId) => {
    try {
      const response = await axiosInstance.post(`/api/savings/admin/withdrawals/${transactionId}/approve`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Reject withdrawal
  rejectWithdrawal: async (transactionId, reason) => {
    try {
      const response = await axiosInstance.post(`/api/savings/admin/withdrawals/${transactionId}/reject`, { reason });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get auto-withdrawal settings
  getAutoWithdrawalSettings: async () => {
    try {
      const response = await axiosInstance.get('/api/savings/admin/auto-withdrawal-settings');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update auto-withdrawal settings
  updateAutoWithdrawalSettings: async (data) => {
    try {
      const response = await axiosInstance.put('/api/savings/admin/auto-withdrawal-settings', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Savings Types Management
  getSavingsTypes: async () => {
    try {
      const response = await axiosInstance.get('/api/savings/admin/types');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  updateSavingsType: async (type, data) => {
    try {
      const response = await axiosInstance.put(`/api/savings/admin/types/${type}`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

export default savingsService;

